/** Utilidades compartidas para agenda de citas del consultorio */

export const ESTADO_CITA_COLOR = {
  pendiente: 'warning',
  confirmada: 'primary',
  realizada: 'success',
  cancelada: 'secondary',
  reagendada: 'info',
};

export const ESTADO_CITA_LABEL = {
  pendiente: 'Pendiente',
  confirmada: 'Confirmada',
  realizada: 'Realizada',
  cancelada: 'Cancelada',
  reagendada: 'Reagendada',
};

export const ROLE_LABEL = {
  doctor: 'Doctor(a)',
  administrador: 'Administrador(a)',
  asistente: 'Asistente',
  recepcionista: 'Recepción',
  viewer: 'Solo lectura',
};

/** Roles que suelen poder figurar como profesional a cargo de una cita. */
export const ROLES_CITA_PROFESIONAL = new Set(['doctor', 'administrador']);

export function resolveDefaultDoctorId(user, equipo = []) {
  if (!user?.id) return '';
  const uid = String(user.id);
  if ((equipo || []).some((m) => String(m.customer_id) === uid)) return uid;
  const clinico = (equipo || []).find((m) => ROLES_CITA_PROFESIONAL.has(m.role));
  if (clinico) return String(clinico.customer_id);
  if (equipo?.length === 1) return String(equipo[0].customer_id);
  return '';
}

/** ID del profesional asignado al paciente (doctor o administrador que atiende). */
export function getPacienteDoctorAsignado(paciente) {
  const id = paciente?.idDoctorAsignado;
  if (id == null || id === '') return '';
  return String(id);
}

export function isProfesionalEquipoValido(equipo, id) {
  if (!id) return false;
  const m = getMiembroEquipo(equipo, id);
  return Boolean(m && ROLES_CITA_PROFESIONAL.has(m.role));
}

/** Prioriza el profesional asignado al paciente; si no hay, el usuario/equipo por defecto. */
export function resolveDoctorForPaciente(paciente, user, equipo = []) {
  const assigned = getPacienteDoctorAsignado(paciente);
  if (assigned && isProfesionalEquipoValido(equipo, assigned)) return assigned;
  return resolveDefaultDoctorId(user, equipo) || (user?.id != null ? String(user.id) : '');
}

export function getMiembroEquipo(equipo, id) {
  if (id == null || id === '') return null;
  return (equipo || []).find((m) => String(m.customer_id) === String(id)) || null;
}

export function formatMiembroEquipoLabel(miembro) {
  if (!miembro) return '—';
  const nombre = miembro.full_name || `ID ${miembro.customer_id}`;
  const rol = miembro.role ? ROLE_LABEL[miembro.role] || miembro.role : '';
  return rol ? `${nombre} (${rol})` : nombre;
}

export function toDateTimeLocalString(date) {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}`;
}

export function addMinutesToLocalDatetime(isoLocal, mins) {
  const date = new Date(isoLocal);
  if (Number.isNaN(date.getTime())) return isoLocal;
  date.setMinutes(date.getMinutes() + mins);
  return toDateTimeLocalString(date);
}

export function getCitaFinDate(c) {
  if (c.fechaHoraFin) {
    const end = new Date(c.fechaHoraFin);
    if (!Number.isNaN(end.getTime())) return end;
  }
  const start = new Date(c.fechaHora);
  return new Date(start.getTime() + 30 * 60000);
}

export function intervalsOverlap(a0, a1, b0, b1) {
  return a0 < b1 && b0 < a1;
}

/** Bloques de 30 min, 08:00–19:00 (misma grilla que el calendario). */
export const CITA_SLOT_HOURS = Array.from({ length: 23 }, (_, i) => {
  const totalMin = 8 * 60 + i * 30;
  const h = String(Math.floor(totalMin / 60)).padStart(2, '0');
  const m = String(totalMin % 60).padStart(2, '0');
  return `${h}:${m}`;
});

export function slotToMinutes(slot) {
  const [h, m] = slot.split(':').map(Number);
  return h * 60 + m;
}

export function getMiembroNombre(equipo, id) {
  if (id == null || id === '') return '—';
  const m = equipo.find((x) => String(x.customer_id) === String(id));
  return m?.full_name || `Profesional #${id}`;
}

/** Formulario de cita a partir de día + franjas elegidas en la grilla (clic o arrastre). */
export function buildCitaFormFromDaySlots(
  date,
  startSlotLabel = null,
  lastIncludedSlotLabel = null,
  userId,
  idPaciente = '',
  equipo = [],
  paciente = null,
) {
  let hour = 9;
  let minute = 0;
  if (startSlotLabel && /^\d{2}:\d{2}$/.test(startSlotLabel)) {
    const parts = startSlotLabel.split(':').map(Number);
    hour = parts[0];
    minute = parts[1];
  }
  const d = new Date(date);
  d.setHours(hour, minute, 0, 0);
  const fechaHora = toDateTimeLocalString(d);

  let fechaHoraFin;
  if (lastIncludedSlotLabel && /^\d{2}:\d{2}$/.test(lastIncludedSlotLabel)) {
    const endMin = slotToMinutes(lastIncludedSlotLabel) + 30;
    const dEnd = new Date(date);
    dEnd.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0);
    fechaHoraFin = toDateTimeLocalString(dEnd);
  } else {
    fechaHoraFin = addMinutesToLocalDatetime(fechaHora, 30);
  }

  return {
    ...createEmptyCitaForm(userId, idPaciente, equipo, paciente),
    fechaHora,
    fechaHoraFin,
  };
}

/** Aplica franjas de la grilla al formulario existente (crear / editar). */
export function applySlotRangeToForm(form, date, startSlotLabel = null, lastIncludedSlotLabel = null) {
  const merged = buildCitaFormFromDaySlots(
    date,
    startSlotLabel,
    lastIncludedSlotLabel,
    form.idDoctorCita,
    form.idPaciente,
  );
  return {
    ...form,
    fechaHora: merged.fechaHora,
    fechaHoraFin: merged.fechaHoraFin,
  };
}

/** Índices de franjas (CITA_SLOT_HOURS) que cubre el rango actual del formulario en un día. */
export function getSlotRangeFromForm(form, dayDate) {
  if (!form?.fechaHora || form.fechaHora.length < 16 || !dayDate) return null;
  const start = new Date(form.fechaHora);
  const day = new Date(dayDate);
  day.setHours(0, 0, 0, 0);
  if (Number.isNaN(start.getTime()) || start.toDateString() !== day.toDateString()) return null;

  const end =
    form.fechaHoraFin && form.fechaHoraFin.length >= 16
      ? new Date(form.fechaHoraFin)
      : getCitaFinDate({ fechaHora: form.fechaHora, fechaHoraFin: form.fechaHoraFin });
  const c0 = start.getHours() * 60 + start.getMinutes();
  const c1 = end.getHours() * 60 + end.getMinutes();

  let fromIdx = null;
  let toIdx = null;
  CITA_SLOT_HOURS.forEach((slot, idx) => {
    const s0 = slotToMinutes(slot);
    const s1 = s0 + 30;
    if (intervalsOverlap(s0, s1, c0, c1)) {
      if (fromIdx === null) fromIdx = idx;
      toIdx = idx;
    }
  });
  if (fromIdx === null) return null;
  return { from: fromIdx, to: toIdx };
}

/** Franjas de la grilla que ocupa una cita (inicio y fin inclusive). */
export function getCitaSlotRange(cita, dayDate = null) {
  if (!cita?.fechaHora) return null;
  const start = new Date(cita.fechaHora);
  if (dayDate) {
    const day = new Date(dayDate);
    day.setHours(0, 0, 0, 0);
    if (Number.isNaN(start.getTime()) || start.toDateString() !== day.toDateString()) return null;
  }
  const end = getCitaFinDate(cita);
  const c0 = start.getHours() * 60 + start.getMinutes();
  const c1 = end.getHours() * 60 + end.getMinutes();
  let fromIdx = null;
  let toIdx = null;
  CITA_SLOT_HOURS.forEach((slot, idx) => {
    const s0 = slotToMinutes(slot);
    const s1 = s0 + 30;
    if (intervalsOverlap(s0, s1, c0, c1)) {
      if (fromIdx === null) fromIdx = idx;
      toIdx = idx;
    }
  });
  if (fromIdx === null) return null;
  return { from: fromIdx, to: toIdx };
}

/** Primera franja de la grilla donde aparece la cita. */
export function getCitaFirstSlotIndex(cita, dayDate = null) {
  return getCitaSlotRange(cita, dayDate)?.from ?? -1;
}

/** Citas activas que bloquean un rango de franjas en un día (canceladas no bloquean). */
export function findCitasBlockingSlotRange(citas, dayDate, fromIdx, toIdx, excludeCitaId = null) {
  if (!dayDate || fromIdx == null || toIdx == null) return [];
  const from = Math.min(fromIdx, toIdx);
  const to = Math.max(fromIdx, toIdx);
  const rangeStart = slotToMinutes(CITA_SLOT_HOURS[from]);
  const rangeEnd = slotToMinutes(CITA_SLOT_HOURS[to]) + 30;
  const dayStr = new Date(dayDate).toDateString();

  return (citas || []).filter((c) => {
    if (c.estado === 'cancelada' || c.estado === 'realizada') return false;
    if (excludeCitaId != null && String(c.id) === String(excludeCitaId)) return false;
    const cStart = new Date(c.fechaHora);
    if (Number.isNaN(cStart.getTime()) || cStart.toDateString() !== dayStr) return false;
    const cEnd = getCitaFinDate(c);
    const c0 = cStart.getHours() * 60 + cStart.getMinutes();
    const c1 = cEnd.getHours() * 60 + cEnd.getMinutes();
    return intervalsOverlap(rangeStart, rangeEnd, c0, c1);
  });
}

/** Citas del mismo día que empiezan después del fin del rango elegido. */
export function findCitasAfterRange(citas, dayDate, fechaFinIso, excludeCitaId = null) {
  if (!dayDate || !fechaFinIso) return [];
  const fin = new Date(fechaFinIso);
  const dayStr = new Date(dayDate).toDateString();
  const finTs = fin.getTime();
  return (citas || [])
    .filter((c) => {
      if (c.estado === 'cancelada') return false;
      if (excludeCitaId != null && String(c.id) === String(excludeCitaId)) return false;
      const start = new Date(c.fechaHora);
      return !Number.isNaN(start.getTime())
        && start.toDateString() === dayStr
        && start.getTime() >= finTs;
    })
    .sort((a, b) => new Date(a.fechaHora) - new Date(b.fechaHora));
}

/** Solo mismo profesional + rangos que se solapan */
export function detectarConflictos(citas, fechaInicioIso, fechaFinIso, idDoctor, exceptId = null) {
  if (!fechaInicioIso || !fechaFinIso || idDoctor == null || idDoctor === '') return [];
  const start = new Date(fechaInicioIso);
  const end = new Date(fechaFinIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return [];
  const t0 = start.getTime();
  const t1 = end.getTime();
  return citas.filter((c) => {
    if (c.id === exceptId) return false;
    if (c.estado === 'cancelada') return false;
    if (String(c.idDoctor) !== String(idDoctor)) return false;
    const c0 = new Date(c.fechaHora).getTime();
    const c1 = getCitaFinDate(c).getTime();
    return intervalsOverlap(t0, t1, c0, c1);
  });
}

export const PENDING_PACIENTE_PREFIX = 'pending-';

export function isPacientePendienteId(id) {
  return String(id || '').startsWith(PENDING_PACIENTE_PREFIX);
}

export function isCitaPacientePendiente(cita) {
  if (!cita) return false;
  return !!(cita.pacientePendiente || isPacientePendienteId(cita.idPaciente));
}

/** Genera id temporal para cita con paciente aún no dado de alta. */
export function makePendingPacienteId(nombre = '') {
  const slug = String(nombre || 'nuevo')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'nuevo';
  return `${PENDING_PACIENTE_PREFIX}${slug}-${Date.now()}`;
}

export function titleCaseNombre(nombre) {
  return String(nombre || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function getPacNombre(pacientes, id) {
  return pacientes.find((p) => String(p.id) === String(id))?.nombre || '—';
}

/** Nombre a mostrar en agenda: registrado o nombre pendiente en la cita. */
export function getCitaPacienteLabel(cita, pacientes = []) {
  if (!cita) return '—';
  const pendingName = (cita.pacienteNombre || '').trim();
  if (pendingName) return pendingName;
  const fromList = getPacNombre(pacientes, cita.idPaciente);
  if (fromList && fromList !== '—') return fromList;
  if (isPacientePendienteId(cita.idPaciente)) return 'Paciente nuevo';
  return '—';
}

export function createEmptyCitaForm(userId, idPaciente = '', equipo = [], paciente = null) {
  const fechaHora = toDateTimeLocalString(new Date());
  const fechaHoraFin = addMinutesToLocalDatetime(fechaHora, 30);
  const defaultDoctor = paciente
    ? resolveDoctorForPaciente(paciente, { id: userId }, equipo)
    : (resolveDefaultDoctorId({ id: userId }, equipo) || (userId != null ? String(userId) : ''));
  return {
    id: null,
    idPaciente: idPaciente ? String(idPaciente) : '',
    pacienteNombre: '',
    pacientePendiente: false,
    fechaHora,
    fechaHoraFin,
    idDoctorCita: defaultDoctor,
    motivo: '',
    notas: '',
    estado: 'pendiente',
  };
}

export function citaToForm(cita) {
  const start = new Date(cita.fechaHora);
  const fechaHora = toDateTimeLocalString(start);
  const fechaHoraFin = cita.fechaHoraFin
    ? toDateTimeLocalString(new Date(cita.fechaHoraFin))
    : addMinutesToLocalDatetime(fechaHora, 30);
  const pendiente = isCitaPacientePendiente(cita);
  return {
    id: cita.id,
    idPaciente: String(cita.idPaciente || ''),
    pacienteNombre: (cita.pacienteNombre || '').trim(),
    pacientePendiente: pendiente,
    fechaHora,
    fechaHoraFin,
    idDoctorCita: cita.idDoctor != null ? String(cita.idDoctor) : '',
    motivo: cita.motivo || '',
    notas: cita.notas || '',
    estado: cita.estado || 'pendiente',
  };
}

export function validateAndBuildCitaPayload(form, userId) {
  const docId = form.idDoctorCita !== '' && form.idDoctorCita != null ? Number(form.idDoctorCita) : userId;
  const fechaHora = form.fechaHora?.length >= 16 ? form.fechaHora : `${(form.fechaHora || '').slice(0, 10)}T09:00`;
  const fechaHoraFin = form.fechaHoraFin?.length >= 16
    ? form.fechaHoraFin
    : addMinutesToLocalDatetime(fechaHora, 30);
  if (new Date(fechaHoraFin) <= new Date(fechaHora)) {
    return { error: 'La hora de fin debe ser posterior a la hora de inicio.' };
  }
  if (docId == null || Number.isNaN(docId)) {
    return { error: 'Selecciona el profesional para la cita.' };
  }

  const nombrePendiente = titleCaseNombre(form.pacienteNombre || '');
  const esPendiente = !!(form.pacientePendiente || isPacientePendienteId(form.idPaciente) || (nombrePendiente && !form.idPaciente));

  if (!form.idPaciente && !nombrePendiente) {
    return { error: 'Seleccioná un paciente o escribí el nombre del paciente nuevo.' };
  }

  let idPaciente = form.idPaciente ? String(form.idPaciente) : '';
  if (esPendiente && nombrePendiente) {
    if (!idPaciente || !isPacientePendienteId(idPaciente)) {
      idPaciente = makePendingPacienteId(nombrePendiente);
    }
  }

  return {
    payload: {
      ...form,
      idPaciente,
      fechaHora,
      fechaHoraFin,
      pacienteNombre: esPendiente ? nombrePendiente : '',
      pacientePendiente: esPendiente,
    },
    docId,
  };
}
